import { Modal, Form, Input, Button } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { updateBulkPlacementModalDisplay } from "@features/modalSlice";
import { useState } from "react";
import {
    updateCampaigns,
} from "@/ts/utils";
import {
    IBulkPlacementModalDisplay,
    IBulkRequiredConfig,
    SubModule,
    TCommonRowData,
    TCampaigns,
    PlacementKey,
} from "@/ts/common-types";
import * as Sentry from "@sentry/react";
import { showToast } from "@/ts/modals";
import { setUpdateGridData } from "@/ts/filters";
import { DATE_BUDGET_UPDATE_CONFIG, placementConfigForUpdateGridData, placementMappings } from "@/ts/constants";
import { map } from "underscore";

type TSelectedRecords = {
    campaignsSelectionData: {
        value: TCommonRowData;
    };
};

const BulkPlacementModal = () => {
    const bulkPlacementModalDisplay = useSelector(
        (state: IBulkPlacementModalDisplay) =>
            state.bulkPlacementModalDisplay.value
    );
    const [inputValue, setInputValue] = useState<Record<string, string>>({
        topOfSearch: null,
        productPage: null,
        restOfSearch: null,
    });
    const [loading, setLoading] = useState(false);
    const { updateRecordId, selectedData } =
        DATE_BUDGET_UPDATE_CONFIG[SubModule.CAMPAIGN];
    const selectedRecords = useSelector(
        (state: TSelectedRecords) =>
            state[selectedData as keyof TSelectedRecords].value
    );

    const handleClose = () => {
        setInputValue({
            topOfSearch: null,
            productPage: null,
            restOfSearch: null,
        });
        dispatch(updateBulkPlacementModalDisplay(false));
    };
    const dispatch = useDispatch();

    const onApplyClickHandler = async () => {
        const campaignIds = map(selectedRecords, "sys_campaign_id");
        if (
            +inputValue.topOfSearch < 0 ||
            +inputValue.productPage < 0 ||
            +inputValue.restOfSearch < 0
        ) {
            showToast("warning", "Placement values cannot be negative.");
            return;
        }

        try {
            setLoading(true);
            const payload = campaignIds.map((campaignId) => {
                const currentCampaign = selectedRecords.find(record => record.sys_campaign_id === campaignId);
                const campaignPayload = {
                    campaignId: campaignId.toString(),
                    state: currentCampaign?.state,
                    attribute: [] as string[],
                } as TCampaigns & {
                    placementTop?: number;
                    placementProductPage?: number;
                    placementRest?: number;
                };

                placementMappings.forEach(({ key, placement }) => {
                    if (inputValue[key]) {
                        (
                            campaignPayload as TCampaigns &
                                Record<PlacementKey, number>
                        )[placement] = +inputValue[key];
                        campaignPayload.attribute.push(placement);
                    }
                });

                return campaignPayload;
            });

            const response = await updateCampaigns(payload);

            if (!response.status) {
                throw new Error(response.message);
            }

            showToast(
                "success",
                `Placements updated for ${campaignIds.length} campaigns`
            );

            const bulkRequiredConfig: IBulkRequiredConfig = {
                selectedRows: selectedRecords,
                paramToFilter: updateRecordId,
                bulkParamsToChange: [],
            };

            placementConfigForUpdateGridData.forEach(({ key, placementKey }) => {
                if (inputValue[key] !== "") {
                    bulkRequiredConfig.bulkParamsToChange.push({
                        key: placementKey,
                        newValue: +inputValue[key],
                        isDateCalcReq: false,
                    });
                }
            });

            setUpdateGridData({
                isReduxState: true,
                bulkRequiredConfig,
            });
            handleClose();
        } catch (e) {
            console.error(e);
            showToast(
                "danger",
                `Error While Updating placements : ${(e as Error).message}`
            );
            Sentry.captureException(e);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            centered
            open={bulkPlacementModalDisplay}
            title="Bulk Placement Change"
            onCancel={handleClose}
            width={600}
            styles={{
                body: {
                    maxHeight: "26vh",
                    overflowY: "auto",
                },
            }}
            footer={[
                <Button
                    type="primary"
                    loading={loading}
                    key="submit"
                    onClick={onApplyClickHandler}
                    disabled={
                        !inputValue.topOfSearch &&
                        !inputValue.productPage &&
                        !inputValue.restOfSearch
                    }
                >
                    Submit
                </Button>,
                <Button key="cancel" onClick={handleClose}>
                    Cancel
                </Button>,
            ]}
        >
            <div>
                <Form
                    name="placement"
                    labelCol={{ span: 8 }}
                    wrapperCol={{ span: 16 }}
                    style={{ maxWidth: 600, paddingTop: "1.5rem" }}
                    autoComplete="off"
                >
                    {placementMappings.map(({ key }, index) => (
                        <Form.Item
                            name={key}
                            label={
                                index === 0
                                    ? "Top of Search"
                                    : index === 1
                                    ? "Product Page"
                                    : "Rest of Search"
                            }
                            key={key}
                        >
                            <Input
                                className="border"
                                type="number"
                                value={inputValue[key]}
                                onChange={(e) =>
                                    setInputValue((prev) => ({
                                        ...prev,
                                        [key]: e.target.value === "" ? null : e.target.value,
                                    }))
                                }
                                style={{ width: "100px" }}
                                suffix={"%"}
                            />
                        </Form.Item>
                    ))}
                </Form>
            </div>
        </Modal>
    );
};

export default BulkPlacementModal;
